import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const KYCStatus = ({ kycData, onDocumentUpload }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [uploadFile, setUploadFile] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-green-600 bg-green-50 border-green-200';
      case 'pending': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'rejected': return 'text-red-600 bg-red-50 border-red-200';
      case 'required': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'rejected': return 'XCircle';
      case 'required': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  const getOverallProgress = () => {
    const totalDocuments = kycData?.documents?.length;
    const verifiedDocuments = kycData?.documents?.filter(doc => doc?.status === 'verified')?.length;
    return Math.round((verifiedDocuments / totalDocuments) * 100);
  };

  const getNextSteps = () => {
    const requiredDocs = kycData?.documents?.filter(doc => doc?.status === 'required');
    const rejectedDocs = kycData?.documents?.filter(doc => doc?.status === 'rejected');
    
    if (rejectedDocs?.length > 0) {
      return `Re-upload ${rejectedDocs?.length} rejected document${rejectedDocs?.length > 1 ? 's' : ''}`;
    }
    if (requiredDocs?.length > 0) {
      return `Upload ${requiredDocs?.length} required document${requiredDocs?.length > 1 ? 's' : ''}`;
    }
    return 'All documents submitted';
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleUploadSubmit = (e) => {
    e?.preventDefault();
    if (uploadFile && selectedDocumentType) {
      onDocumentUpload(selectedDocumentType, uploadFile);
      setShowUploadModal(false);
      setUploadFile(null);
      setSelectedDocumentType('');
    }
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">KYC Verification</h2>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(kycData?.overallStatus)}`}>
            {kycData?.overallStatus === 'verified' ? 'Fully Verified' : 
             kycData?.overallStatus === 'pending' ? 'Under Review' :
             kycData?.overallStatus === 'partial' ? 'Partially Complete' : 'Not Started'}
          </div>
        </div>
      </div>
      <div className="p-6">
        {/* Overall Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Verification Progress</span>
            <span className="text-sm text-gray-600">{getOverallProgress()}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${getOverallProgress()}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600 mt-2">{getNextSteps()}</p>
        </div>

        {/* Document List */}
        <div className="space-y-4">
          {kycData?.documents?.map((document) => (
            <div
              key={document?.type}
              className={`border rounded-lg p-4 ${getStatusColor(document?.status)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="mt-1">
                    <Icon name={getStatusIcon(document?.status)} size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{document?.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{document?.description}</p>
                    
                    {document?.status === 'verified' && document?.verifiedAt && (
                      <div className="flex items-center space-x-1 mt-2 text-sm text-green-600">
                        <Icon name="CheckCircle" size={14} />
                        <span>Verified on {formatDate(document?.verifiedAt)}</span>
                      </div>
                    )}
                    
                    {document?.status === 'pending' && document?.submittedAt && (
                      <div className="flex items-center space-x-1 mt-2 text-sm text-amber-600">
                        <Icon name="Clock" size={14} />
                        <span>Submitted on {formatDate(document?.submittedAt)} - Under review</span>
                      </div>
                    )}
                    
                    {document?.status === 'rejected' && document?.rejectionReason && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        <strong>Rejection reason:</strong> {document?.rejectionReason}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {(document?.status === 'required' || document?.status === 'rejected') && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Upload"
                      iconPosition="left"
                      onClick={() => {
                        setSelectedDocumentType(document?.type);
                        setShowUploadModal(true);
                      }}
                    >
                      Upload
                    </Button>
                  )}
                  
                  {document?.status === 'verified' && document?.downloadUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Download"
                      onClick={() => window.open(document?.downloadUrl, '_blank')}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Compliance Information */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900 mb-1">Why do we need these documents?</h3>
              <p className="text-sm text-blue-700">
                We're required by financial regulations to verify your identity and address. 
                This helps us protect your account and comply with anti-money laundering laws. 
                All documents are encrypted and stored securely.
              </p>
            </div>
          </div>
        </div>

        {/* Account Limitations */}
        {kycData?.overallStatus !== 'verified' && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-900 mb-1">Account Limitations</h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Withdrawal limit: $1,000 per day</li>
                  <li>• Deposit limit: $5,000 per month</li>
                  <li>• Some trading features may be restricted</li>
                </ul>
                <p className="text-sm text-amber-700 mt-2">
                  Complete verification to remove these limitations.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Upload Document</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setShowUploadModal(false)}
                />
              </div>
            </div>

            <form onSubmit={handleUploadSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Type
                  </label>
                  <select
                    value={selectedDocumentType}
                    onChange={(e) => setSelectedDocumentType(e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select document type</option>
                    {kycData?.documents?.filter(doc => doc?.status === 'required' || doc?.status === 'rejected')?.map(doc => (
                        <option key={doc?.type} value={doc?.type}>
                          {doc?.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload File
                  </label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Accepted formats: JPG, PNG, PDF (max 5MB)
                  </p>
                </div>

                {uploadFile && (
                  <div className="p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center space-x-2">
                      <Icon name="File" size={16} className="text-gray-600" />
                      <span className="text-sm text-gray-900">{uploadFile?.name}</span>
                      <span className="text-xs text-gray-500">
                        ({(uploadFile?.size / 1024 / 1024)?.toFixed(2)} MB)
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  className="flex-1"
                >
                  Upload Document
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCStatus;